import React from 'react';
import { motion } from 'framer-motion';
export function ProblemSection() {
  return <section className="relative py-28 md:py-36 px-4 overflow-hidden bg-[#faf8f5]" data-id="element-184">
      {/* Warm radial background */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-185">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-amber-100/40 rounded-full blur-[120px]" data-id="element-186"></div>
        <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-green-100/30 rounded-full blur-[100px]" data-id="element-187"></div>
      </div>

      <div className="container mx-auto max-w-2xl relative z-10" data-id="element-188">
        {/* Heading */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-center mb-16" data-id="element-189">
          <div className="inline-flex items-center gap-2 text-amber-700/70 text-sm font-semibold tracking-widest uppercase mb-6" data-id="element-190">
            <span className="w-8 h-px bg-amber-400/60" data-id="element-191"></span>
            Tu camino empieza aquí
            <span className="w-8 h-px bg-amber-400/60" data-id="element-192"></span>
          </div>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-900 leading-tight" data-id="element-193">
            Si llegaste hasta acá,
            <br data-id="element-194" />
            no fue por casualidad
          </h2>
        </motion.div>

        {/* Opening question — styled as a quote card */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="relative bg-white/80 backdrop-blur-sm rounded-2xl px-8 py-8 md:px-10 md:py-10 mb-14 border border-amber-200/50 shadow-[0_4px_40px_rgba(217,119,6,0.06)]" data-id="element-195">
          <span className="absolute -top-4 left-8 text-6xl font-serif text-amber-300/50 leading-none select-none" data-id="element-196">
            "
          </span>
          <p className="text-xl md:text-2xl font-serif italic text-gray-700 leading-relaxed text-center pt-2" data-id="element-197">
            ¿Sentís que hay algo en vos que quiere sanar… pero no sabés bien por
            dónde empezar?
          </p>
          <span className="absolute -bottom-6 right-8 text-6xl font-serif text-amber-300/50 leading-none select-none rotate-180" data-id="element-198">
            "
          </span>
        </motion.div>

        {/* Core message flow */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.15
      }} className="text-center space-y-8 mb-14" data-id="element-199">
          <p className="text-lg md:text-xl text-gray-500 leading-relaxed" data-id="element-200">
            No es falta de espiritualidad. No es falta de sensibilidad.
          </p>

          {/* Key phrase — highlighted block */}
          <div className="relative inline-block" data-id="element-201">
            <div className="absolute inset-0 bg-amber-100/60 rounded-xl -rotate-[0.5deg] scale-[1.02]" data-id="element-202"></div>
            <p className="relative text-2xl md:text-3xl font-extrabold text-gray-900 px-6 py-4" data-id="element-203">
              👉 Es falta de orden y guía clara.
            </p>
          </div>
        </motion.div>

        {/* Ornamental divider */}
        <div className="flex items-center justify-center gap-3 my-12 opacity-40" data-id="element-204">
          <div className="h-px w-16 bg-gradient-to-r from-transparent to-amber-500" data-id="element-205"></div>
          <div className="w-1.5 h-1.5 rotate-45 bg-amber-500" data-id="element-206"></div>
          <div className="w-2.5 h-2.5 rotate-45 border border-amber-500" data-id="element-207"></div>
          <div className="w-1.5 h-1.5 rotate-45 bg-amber-500" data-id="element-208"></div>
          <div className="h-px w-16 bg-gradient-to-l from-transparent to-amber-500" data-id="element-209"></div>
        </div>

        {/* Continuation text */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="text-center space-y-8 mb-16" data-id="element-210">
          <p className="text-lg md:text-xl text-gray-500 leading-relaxed max-w-lg mx-auto" data-id="element-211">
            Leés información suelta. Probás meditaciones. Sentís energía… pero
            dudás si lo estás haciendo bien.
          </p>

          <p className="text-xl md:text-2xl font-bold text-gray-900 font-serif" data-id="element-212">
            Y eso frena tu proceso…
          </p>

          {/* Thin line */}
          <div className="w-20 h-px bg-gray-300 mx-auto" data-id="element-213"></div>

          <div className="pt-2" data-id="element-214">
            <p className="text-2xl md:text-3xl font-bold text-gray-900 font-serif leading-snug" data-id="element-215">
              Reiki no es complicado.
            </p>
            <p className="text-lg text-amber-700/80 mt-3 font-medium" data-id="element-216">
              Lo complicado es aprenderlo sin una guía clara.
            </p>
          </div>
        </motion.div>

        {/* Pain points card */}
        <motion.div initial={{
        opacity: 0,
        y: 25
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.25
      }} className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden max-w-xl mx-auto" data-id="element-217">
          {/* Card header */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-8 py-5 text-center" data-id="element-218">
            <h3 className="text-white font-serif font-bold text-lg" data-id="element-219">
              A muchas personas les pasa esto:
            </h3>
          </div>

          {/* Card body */}
          <div className="p-8 md:p-10" data-id="element-220">
            <ul className="space-y-5" data-id="element-221">
              {['Encuentran información suelta y contradictoria', 'Practican, pero dudan si lo hacen bien', 'Les cuesta sostener constancia', 'Sienten que podrían avanzar más'].map((item, idx) => <motion.li key={idx} initial={{
              opacity: 0,
              x: -10
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: 0.3 + idx * 0.08
            }} className="flex items-center gap-4 text-gray-700 text-lg" data-id="element-222">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-red-50 border border-red-200 flex items-center justify-center" data-id="element-223">
                    <span className="text-red-400 text-xs font-bold" data-id="element-224">✕</span>
                  </span>
                  <span data-id="element-225">{item}</span>
                </motion.li>)}
            </ul>

            {/* Card footer message */}
            <div className="mt-10 pt-8 border-t border-gray-100" data-id="element-226">
              <p className="text-center text-lg leading-relaxed" data-id="element-227">
                <span className="text-gray-600" data-id="element-228">
                  El problema no es la falta de sensibilidad.
                </span>
                <br data-id="element-229" />
                <span className="font-bold text-amber-700 text-xl" data-id="element-230">
                  Es no tener un camino ordenado para avanzar con seguridad.
                </span>
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}